import { useEffect, useRef } from "react";
import { useTenant } from "./TenantProvider";
import { setLastUsedTenantId } from "./last-used-tenant-store";

/**
 * Validates that a tenant ID from URL params is valid and triggers selection.
 * When the URL contains a valid tenant that isn't currently selected,
 * this hook updates lastUsedTenantId which causes TenantProvider to select it.
 *
 * @param urlTenantId - Tenant ID from URL params
 * @returns Object with isValidating, isValid, and error state
 */
export function useValidateTenantFromUrl(urlTenantId: string | undefined): {
  isValidating: boolean;
  isValid: boolean;
  error: Error | null;
  refreshTenants: () => Promise<void>;
} {
  const { tenants, selectedTenant, isLoading, error, refreshTenants } = useTenant();
  const hasTriggeredSelection = useRef<string | null>(null);

  // When URL tenant is valid but not selected, trigger selection via lastUsedTenantId
  useEffect(() => {
    if (isLoading || !urlTenantId || tenants.length === 0) return;
    if (selectedTenant?.id === urlTenantId) return;
    if (hasTriggeredSelection.current === urlTenantId) return;

    const urlTenantExists = tenants.some((t) => t.id === urlTenantId);
    if (urlTenantExists) {
      hasTriggeredSelection.current = urlTenantId;
      void setLastUsedTenantId(urlTenantId);
    }
  }, [urlTenantId, selectedTenant?.id, tenants, isLoading]);

  // Still loading tenants
  if (isLoading) {
    return { isValidating: true, isValid: false, error, refreshTenants };
  }

  // No tenants available
  if (tenants.length === 0) {
    return { isValidating: false, isValid: false, error, refreshTenants };
  }

  // No URL tenant ID provided
  if (!urlTenantId) {
    return { isValidating: false, isValid: false, error, refreshTenants };
  }

  // URL tenant matches selected tenant - valid
  if (selectedTenant?.id === urlTenantId) {
    return { isValidating: false, isValid: true, error, refreshTenants };
  }

  // URL tenant exists but not selected yet - selection has been triggered
  const urlTenantExists = tenants.some((t) => t.id === urlTenantId);
  if (urlTenantExists) {
    return { isValidating: true, isValid: false, error, refreshTenants };
  }

  // URL tenant not found - invalid
  return { isValidating: false, isValid: false, error, refreshTenants };
}
